import { Alert, Button, Drawer, Input, List, Progress, Space, Table, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useDebouncedState } from '../../../hooks/useDebouncedState'
import { ItemClasses, itemClassName } from '../../../common/constants'
import { useInvalidateItemCatalog, useItemCatalogQuery, useRetireCatalogItemMutation } from '../../ItemCatalog/apiQueries'
import { CatalogItem } from '../../../domain/catalogItem'
import {
  applyRetireOutcome,
  calculatePaginationTotal,
  deriveRetireAlertState,
  extractRetireErrorMessage,
  isRetireInProgress,
  RetireProgress,
  startRetireProgress
} from './bulkRetireModel'

const DEFAULT_PAGE_SIZE = 100
const PAGE_SIZE_OPTIONS = ['25', '50', '100', '200']

type Props = {
  open: boolean
  onClose: () => void
}

type SelectedItem = { id: number, name: string }

const BulkRetireDrawer = ({ open, onClose }: Props) => {
  const { state: searchInput, debouncedState: debouncedSearch, setState: setSearchInput } = useDebouncedState('', 250)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])
  const [retireProgress, setRetireProgress] = useState<RetireProgress | null>(null)

  const classIds = useMemo(() => Object.values(ItemClasses), [])
  const itemsQuery = useItemCatalogQuery(debouncedSearch, pageSize, classIds, [], false)
  const retireMutation = useRetireCatalogItemMutation({ skipInvalidation: true })
  const invalidateItemCatalog = useInvalidateItemCatalog()

  const pages = itemsQuery.data?.pages ?? []
  const lastPage = pages[pages.length - 1]
  const hasMore = lastPage !== undefined && lastPage.length === pageSize
  const currentPageItems = pages[currentPage - 1] ?? []

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch])

  useEffect(() => {
    if (!open) {
      setSearchInput('')
      setPageSize(DEFAULT_PAGE_SIZE)
      setCurrentPage(1)
      setSelectedItems([])
      setRetireProgress(null)
    }
  }, [open])

  const isRetiring = isRetireInProgress(retireProgress)
  const selectedRowKeys = selectedItems.map(s => s.id)

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize)
      setCurrentPage(1)
      return
    }
    setCurrentPage(nextPage)
    if (nextPage > pages.length && hasMore) {
      itemsQuery.fetchNextPage()
    }
  }

  const handleSelectionChange = (keys: React.Key[], rows: CatalogItem[]) => {
    const nextIds = keys as number[]
    const rowsById = new Map(rows.map(r => [r.id, { id: r.id, name: r.name }]))
    setSelectedItems(prev => {
      const prevById = new Map(prev.map(s => [s.id, s]))
      return nextIds.map(id => rowsById.get(id) ?? prevById.get(id) ?? { id, name: `#${id}` })
    })
  }

  const handleRetire = async () => {
    const items = [...selectedItems]
    setRetireProgress(startRetireProgress(items.length))

    for (const item of items) {
      try {
        await retireMutation.mutateAsync(item.id)
        setRetireProgress(prev => prev && applyRetireOutcome(prev, { kind: 'success' }))
      } catch (err) {
        const error = extractRetireErrorMessage(err)
        setRetireProgress(prev => prev && applyRetireOutcome(prev, { kind: 'failure', id: item.id, name: item.name, error }))
      }
    }

    setSelectedItems([])
    invalidateItemCatalog()
  }

  const handleClose = () => {
    if (isRetiring) {
      return
    }
    onClose()
  }

  const handleDismissProgress = () => {
    setRetireProgress(null)
  }

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
    {
      title: 'Class',
      key: 'class',
      render: (_: unknown, item: CatalogItem) => itemClassName(item.class)
    },
    {
      title: 'Categories',
      key: 'categories',
      render: (_: unknown, item: CatalogItem) => item.categories.join(', ')
    }
  ]

  const alertState = retireProgress !== null ? deriveRetireAlertState(retireProgress) : null

  return <Drawer
    title="Bulk Retire Items"
    placement="right"
    width={1100}
    open={open}
    onClose={handleClose}
    maskClosable={!isRetiring}
    closable={!isRetiring}
  >
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Input.Search
          placeholder="Search items"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          allowClear
          style={{ width: 320 }}
        />
        <Button
          type="primary"
          danger
          disabled={selectedItems.length === 0 || isRetiring}
          loading={isRetiring}
          onClick={handleRetire}
        >
          Retire Selected ({selectedItems.length})
        </Button>
      </Space>

      {retireProgress !== null && alertState !== null && <Alert
        type={alertState.type}
        showIcon
        message={alertState.message}
        description={<>
          <Progress percent={Math.round((retireProgress.completed / retireProgress.total) * 100)} />
          <Typography.Text>
            {retireProgress.completed} of {retireProgress.total} processed
            {retireProgress.failures.length > 0 && ` (${retireProgress.failures.length} failed)`}
          </Typography.Text>
          {retireProgress.failures.length > 0 && <List
            size="small"
            style={{ marginTop: 12 }}
            dataSource={retireProgress.failures}
            renderItem={failure => <List.Item>
              <Typography.Text strong>{failure.name}</Typography.Text>: {failure.error}
            </List.Item>}
          />}
        </>}
        action={!isRetiring && <Button size="small" onClick={handleDismissProgress}>Dismiss</Button>}
      />}

      <Table<CatalogItem>
        dataSource={currentPageItems}
        columns={columns}
        rowKey="id"
        loading={itemsQuery.isLoading || itemsQuery.isFetchingNextPage}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: handleSelectionChange,
          preserveSelectedRowKeys: true
        }}
        pagination={{
          current: currentPage,
          pageSize,
          total: calculatePaginationTotal(pages.length, hasMore, pageSize),
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showQuickJumper: false,
          onChange: handlePaginationChange
        }}
        size="small"
      />
      <div style={{ height: 50 }} />
    </Space>
  </Drawer>
}

export default BulkRetireDrawer
